import { apiFetch } from './client';

// ---------------------------------------------------------------------------
// Types
//
// These interfaces mirror the backend serializers in
// backend/app/routers/messaging.py EXACTLY (the backend is the contract
// source of truth). Key contract facts:
//   * _serialize_channel returns is_archived (not "archived") and does NOT
//     include phi_free_attested_by/at, updated_at, or member_ids.
//   * list_messages items have an INTEGER id and never include deleted_at
//     (soft-deleted messages are filtered server-side).
//   * GET /unread channel entries are {channel_id, unread, mentions}.
//   * Retention/attestation writes go to PATCH /channels/{id}/retention
//     (RetentionPatch: retention_years, phi_free_attested, records_series) —
//     NOT the generic PATCH /channels/{id}, whose ChannelPatch silently
//     ignores retention fields.
// ---------------------------------------------------------------------------

export type ChannelKind = 'channel' | 'dm' | 'group_dm';

export interface Channel {
  id: string;
  kind: ChannelKind;
  name: string | null;
  topic: string | null;
  history_visible_to_new_members: boolean;
  is_archived: boolean;
  retention_years: number | null;
  /** [rev 11, item 8] custodian attestation that this channel carries no PHI. */
  phi_free_attested: boolean;
  created_at: string | null;
}

export interface Message {
  id: number;
  channel_id: string;
  author_id: string | null;
  body: string;
  created_at: string | null;
  edited_at: string | null;
}

export interface MessagesPage {
  messages: Message[];
  has_more: boolean;
}

/** POST /channels/{id}/messages response — a slim ack, not a full Message. */
export interface PostMessageResponse {
  id: number;
  channel_id: string;
  author_id: string;
  created_at: string | null;
}

export interface UnreadChannelEntry {
  channel_id: string;
  unread: number;
  mentions: number;
}

export interface UnreadResponse {
  channels: UnreadChannelEntry[];
  total_unread: number;
  has_any_mention: boolean;
}

export interface MemberLite {
  id: string;
  display_name: string;
}

export interface MemberTag {
  id: string;
  name: string;
}

export type LegalHoldScopeType = 'tenant' | 'channel' | 'user' | 'date_range' | 'compound';

export interface LegalHold {
  id: string;
  scope_type: LegalHoldScopeType;
  channel_id: string | null;
  user_id: string | null;
  reason: string;
  released_at: string | null;
}

export interface CreateLegalHoldRequest {
  scope_type: LegalHoldScopeType;
  channel_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  reason: string;
}

export interface ChannelRetentionUpdateRequest {
  retention_years?: number;
  phi_free_attested?: boolean;
  /**
   * Custodian attestation note (records series per the adopted records
   * schedule, step 1.19a / § Approach 4c item 1). Backend field name:
   * RetentionPatch.records_series — it feeds the channel_phi_attestation
   * audit row's details.
   */
  records_series?: string;
}

export interface ExportScope {
  scope_type: LegalHoldScopeType;
  channel_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
}

export const messagingApi = {
  // -------------------------------------------------------------------
  // Channels (step 1.8)
  // -------------------------------------------------------------------
  listChannels: () =>
    apiFetch<{ channels: Channel[] }>('/api/messaging/channels'),

  createChannel: (data: { kind: ChannelKind; name?: string; topic?: string; member_ids?: string[]; history_visible_to_new_members?: boolean }) =>
    apiFetch<Channel>('/api/messaging/channels', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateChannel: (id: string, data: { name?: string; topic?: string; is_archived?: boolean }) =>
    apiFetch<Channel>(`/api/messaging/channels/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * [rev 4/11/12/13/14, § Approach 4c] Retention override + PHI-free
   * attestation write path. MUST target the dedicated /retention endpoint
   * (set_retention) — the generic PATCH /channels/{id} (patch_channel)
   * silently drops retention fields and would return a false 200.
   */
  updateChannelRetention: (id: string, data: ChannelRetentionUpdateRequest) =>
    apiFetch<Channel>(`/api/messaging/channels/${id}/retention`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  addChannelMember: (id: string, userId: string) =>
    apiFetch<{ status: string }>(`/api/messaging/channels/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  // -------------------------------------------------------------------
  // Messages (step 1.9)
  // -------------------------------------------------------------------
  getMessages: (channelId: string, opts: { beforeId?: number; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.beforeId !== undefined) params.set('before_id', String(opts.beforeId));
    params.set('limit', String(opts.limit ?? 50));
    return apiFetch<MessagesPage>(`/api/messaging/channels/${channelId}/messages?${params.toString()}`);
  },

  postMessage: (channelId: string, body: string) =>
    apiFetch<PostMessageResponse>(`/api/messaging/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  editMessage: (messageId: number, body: string) =>
    apiFetch<{ id: number; edited_at: string }>(`/api/messaging/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),

  deleteMessage: (messageId: number) =>
    apiFetch<{ status: string }>(`/api/messaging/messages/${messageId}`, {
      method: 'DELETE',
    }),

  // -------------------------------------------------------------------
  // Unread / snooze (steps 1.10-1.11)
  // -------------------------------------------------------------------
  getUnread: () =>
    apiFetch<UnreadResponse>('/api/messaging/unread'),

  /**
   * Advance the read cursor. up_to_message_id is honored server-side
   * (clamped: never beyond the channel's latest id, never backward);
   * omitting it marks the whole channel read.
   */
  markRead: (channelId: string, upToMessageId?: number) =>
    apiFetch<{ status: string; last_read_message_id: number | null }>(`/api/messaging/channels/${channelId}/read`, {
      method: 'POST',
      body: JSON.stringify(upToMessageId !== undefined ? { up_to_message_id: upToMessageId } : {}),
    }),

  snoozeChannel: (channelId: string, minutes: 30 | 60 | 90) =>
    apiFetch<{ status: string; muted_until: string }>(`/api/messaging/channels/${channelId}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ minutes }),
    }),

  // -------------------------------------------------------------------
  // Mention typeahead (step 1.12) — non-admin-gated, VIEW_MESSAGING only.
  // -------------------------------------------------------------------
  getMembersLite: (q: string) =>
    apiFetch<{ members: MemberLite[] }>(`/api/messaging/members-lite?q=${encodeURIComponent(q)}`),

  // -------------------------------------------------------------------
  // Tags (step 1.14a) — require_messaging_admin
  // -------------------------------------------------------------------
  listTags: () =>
    apiFetch<{ tags: MemberTag[] }>('/api/messaging/tags'),

  createTag: (name: string) =>
    apiFetch<MemberTag>('/api/messaging/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  deleteTag: (id: string) =>
    apiFetch<{ status: string }>(`/api/messaging/tags/${id}`, { method: 'DELETE' }),

  assignTagMember: (tagId: string, userId: string) =>
    apiFetch<{ status: string }>(`/api/messaging/tags/${tagId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  unassignTagMember: (tagId: string, userId: string) =>
    apiFetch<{ status: string }>(`/api/messaging/tags/${tagId}/members/${userId}`, {
      method: 'DELETE',
    }),

  // -------------------------------------------------------------------
  // Legal holds (step 1.14b) — require_messaging_admin
  // -------------------------------------------------------------------
  listLegalHolds: () =>
    apiFetch<{ legal_holds: LegalHold[] }>('/api/messaging/legal-holds'),

  createLegalHold: (data: CreateLegalHoldRequest) =>
    apiFetch<{ id: string; scope_type: LegalHoldScopeType }>('/api/messaging/legal-holds', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  releaseLegalHold: (id: string) =>
    apiFetch<{ status: string }>(`/api/messaging/legal-holds/${id}/release`, {
      method: 'POST',
    }),

  // -------------------------------------------------------------------
  // Export / redaction review set (step 1.14d) — require_messaging_admin
  // -------------------------------------------------------------------
  exportMessages: (scope: ExportScope) =>
    apiFetch<Response>('/api/messaging/export', {
      method: 'POST',
      body: JSON.stringify(scope),
      raw: true,
    }),
};
